/**
 * Installing the PWA (03 §16): the menu item (`installAvailable` + `promptInstall()`) and the iOS Safari guide
 * (Share → Add to Home Screen) it opens. There is no unprompted install hint (ADR 0020): installing starts only from
 * the menu or the browser's own install UI.
 */
import { computed, signal } from '@preact/signals';
import { t, tHtml } from '@tarock/i18n';
import { useEffect, useRef } from 'preact/hooks';

const KEY = 'tarock.install.v1';

/** What is remembered about installing, per browser. */
interface InstallRecord {
  installed: boolean;
}

function load(): InstallRecord | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as InstallRecord) : null;
  } catch {
    return null;
  }
}

function save(r: InstallRecord): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(r));
  } catch {
    // private mode: the install is not remembered
  }
}

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true || (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches)
  );
}

// records from before ADR 0020 carry more fields; only `installed` is still read
const record = signal<InstallRecord>({ installed: load()?.installed === true });
const deferred = signal<InstallPromptEvent | null>(null);
/** The iOS guide dialog is open. */
const iosGuideOpen = signal(false);

/** "Install app" can be offered (menu item `[data-testid=install-app]`). */
export const installAvailable = computed(
  () => !record.value.installed && !isStandalone() && (deferred.value !== null || isIosSafari()),
);

let listening = false;
/** Captures `beforeinstallprompt` (idempotent; call it early at boot so the event is not missed). */
export function initInstallPrompt(): void {
  if (listening || typeof window === 'undefined') {
    return;
  }
  listening = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred.value = e as InstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    deferred.value = null;
    update({ installed: true });
  });
}
initInstallPrompt();

function update(patch: Partial<InstallRecord>): void {
  record.value = { ...record.value, ...patch };
  save(record.value);
}

/** The browser's install prompt, or the iOS guide (from the menu item only). */
export async function promptInstall(): Promise<void> {
  const e = deferred.value;
  if (!e) {
    if (isIosSafari()) {
      iosGuideOpen.value = true;
    }
    return;
  }
  deferred.value = null;
  await e.prompt();
  const choice = await e.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
  if (choice.outcome === 'accepted') {
    update({ installed: true });
  }
}

function IosGuide() {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) {
      d.showModal?.();
    }
  }, []);
  const close = () => {
    iosGuideOpen.value = false;
  };
  return (
    <dialog ref={ref} data-testid="ios-install-guide" aria-label={t('pwa.install')} onClose={close}>
      <h2>{t('pwa.install')}</h2>
      <p dangerouslySetInnerHTML={{ __html: tHtml('pwa.iosGuide') }} />
      <button type="button" class="btn" onClick={close}>
        {t('common.close')}
      </button>
    </dialog>
  );
}

/** The iOS guide dialog while the menu item has it open (TablePage, next to the menu). */
export function IosInstallGuide() {
  return iosGuideOpen.value ? <IosGuide /> : null;
}
